import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Card, CardBody, Col, Container, Row, Modal, ModalHeader, ModalBody,
    Button, Input, Label, Spinner, Badge,
} from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { checkout, getAllPacks, getCoinList } from '../../../helpers/fakebackend_helper';

// Tier themes: cheapest → most expensive. Index assigned by sorted price.
// Unified dark + accent palette; "kind" controls subtle styling differences.
const TIER_THEMES = [
    { name: "starter", kind: "muted",    icon: "ri-leaf-line" },
    { name: "basic",   kind: "muted",    icon: "ri-flashlight-line" },
    { name: "pro",     kind: "default",  icon: "ri-rocket-2-line" },
    { name: "premium", kind: "default",  icon: "ri-star-fill" },
    { name: "elite",   kind: "accent",   icon: "ri-vip-crown-2-fill" },
    { name: "vip",     kind: "accent",   icon: "ri-vip-diamond-fill" },
];

const Plan = () => {
    document.title = "Plans | ApiHub.today";

    const [packs, setPacks] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [coins, setCoins] = useState([]);
    const [selectedCoin, setSelectedCoin] = useState(null);
    const [selectedChain, setSelectedChain] = useState(null);
    const [selectedPack, setSelectedPack] = useState(null);
    const [loading, setLoading] = useState(false);

    const togglePaymentModal = () => setShowPaymentModal(s => !s);

    const btnCheckoutClick = async (pack) => {
        setSelectedPack(pack);
        const coinsList = await getCoinList();
        setCoins(coinsList.data || []);
        setSelectedCoin(null);
        setSelectedChain(null);
        setShowPaymentModal(true);
    };

    const btnGetPaymentClick = async () => {
        setLoading(true);
        try {
            const response = await checkout(selectedPack.id, selectedCoin.code, selectedChain);
            if (response.success) {
                window.location = response.data.checkout_url;
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const loadPacks = async () => {
        const res = await getAllPacks();
        if (res && res.success) setPacks(res.data || []);
    };

    useEffect(() => { loadPacks(); }, []);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Plans" pageTitle="Pages" />

                    {/* Hero */}
                    <div className="ah-hero-card mb-4">
                        <div className="ah-hero-inner">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <div className="ah-hero-eyebrow">Plans</div>
                                    <h1 className="ah-hero-title">Choose your plan</h1>
                                    <p className="ah-hero-sub">
                                        Top up credits with a one-time purchase. Better rates on bigger packs.
                                    </p>
                                    <div className="d-flex gap-2 flex-wrap mt-3">
                                        <Link to="/pricing" className="btn btn-outline-secondary fw-medium">
                                            <i className="ri-price-tag-3-line me-1"></i> See per-API pricing
                                        </Link>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Plan cards — CSS grid auto-fit so no wrapping orphans */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                            gap: "1.25rem",
                        }}
                    >
                        {packs
                            .slice()
                            .sort((a, b) => (a.price || 0) - (b.price || 0))
                            .map((pack, idx, arr) => {
                                const themeIdx = Math.min(
                                    Math.floor((idx / Math.max(arr.length - 1, 1)) * (TIER_THEMES.length - 1)),
                                    TIER_THEMES.length - 1
                                );
                                // Use the second cheapest plan (idx 1 = "basic") as baseline; skip the starter trial pack.
                                const baseIdx = Math.min(1, arr.length - 1);
                                const baseRate = arr[baseIdx].price > 0 ? arr[baseIdx].credit / arr[baseIdx].price : 0;
                                const thisRate = pack.price > 0 ? pack.credit / pack.price : 0;
                                const bonusPct = baseRate > 0 ? Math.round(((thisRate / baseRate) - 1) * 100) : 0;
                                return (
                                    <PlanCard
                                        key={pack.id}
                                        pack={pack}
                                        theme={TIER_THEMES[themeIdx]}
                                        bonusPct={bonusPct}
                                        onSelect={() => btnCheckoutClick(pack)}
                                    />
                                );
                            })}
                        {packs.length === 0 && (
                            <Card>
                                <CardBody className="text-center py-5">
                                    <Spinner color="primary" />
                                    <p className="text-muted mt-3 mb-0">Loading plans...</p>
                                </CardBody>
                            </Card>
                        )}
                    </div>
                </Container>

                {/* Payment Modal */}
                <Modal
                    className="modal-lg"
                    isOpen={showPaymentModal}
                    toggle={togglePaymentModal}
                    centered
                >
                    <ModalHeader toggle={togglePaymentModal}>
                        Payment{selectedPack ? ` — ${selectedPack.name} ($${selectedPack.price.toLocaleString("en-US")})` : ""}
                    </ModalHeader>
                    <ModalBody className="p-4">
                        <Card className="border shadow-none mb-3">
                            <CardBody className="p-3">
                                <Label className="fw-medium mb-2">Select payment coin</Label>
                                <Row className="g-2">
                                    {coins.map((coin, index) => (
                                        <Col lg={3} sm={4} key={coin.code}>
                                            <div className="form-check card-radio">
                                                <Input
                                                    id={"paymentMethod" + index}
                                                    name="paymentMethod"
                                                    type="radio"
                                                    value={index}
                                                    className="form-check-input"
                                                    onChange={(event) => {
                                                        const tmp = coins[Number(event.target.value)];
                                                        const arr = JSON.parse(tmp.chains);
                                                        if (arr.indexOf(selectedChain) < 0) {
                                                            setSelectedChain(arr[0]);
                                                        }
                                                        setSelectedCoin(tmp);
                                                    }}
                                                />
                                                <Label className="form-check-label d-flex align-items-center gap-2" htmlFor={"paymentMethod" + index}>
                                                    <img style={{ width: 20 }} src={coin.icon} alt={coin.code} />
                                                    <span className="fs-14">{coin.code}</span>
                                                </Label>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </CardBody>
                        </Card>

                        {selectedCoin && (
                            <Card className="border shadow-none mb-3">
                                <CardBody className="p-3">
                                    <Label className="fw-medium mb-2">Select chain</Label>
                                    <Row className="g-2">
                                        {JSON.parse(selectedCoin.chains).map((chain, index) => (
                                            <Col lg={3} sm={4} key={chain}>
                                                <div className="form-check card-radio">
                                                    <Input
                                                        id={"chain" + index}
                                                        name="chain"
                                                        type="radio"
                                                        value={chain}
                                                        checked={selectedChain === chain}
                                                        onChange={(event) => setSelectedChain(event.target.value)}
                                                        className="form-check-input"
                                                    />
                                                    <Label className="form-check-label" htmlFor={"chain" + index}>
                                                        <span className="fs-14">{chain}</span>
                                                    </Label>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </CardBody>
                            </Card>
                        )}

                        <div className="d-grid">
                            <Button
                                disabled={!selectedCoin || !selectedChain || loading}
                                color="primary"
                                onClick={btnGetPaymentClick}
                            >
                                {loading
                                    ? <><Spinner size="sm" className="me-2" /> Processing...</>
                                    : <>Continue <i className="ri-arrow-right-line ms-1" /></>}
                            </Button>
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        </React.Fragment>
    );
};

const PlanCard = ({ pack, theme, bonusPct = 0, onSelect }) => {
    const isPopular = !!pack.is_popular;
    const kindClass = `ah-tier ah-tier-${theme.kind}` + (isPopular ? " is-featured" : "");

    return (
        <div className={kindClass}>
            {isPopular && (
                <div className="ah-tier-popular">
                    <i className="ri-star-fill me-1" />Popular
                </div>
            )}

            {/* Header */}
            <div className="ah-tier-head">
                <div className="ah-tier-icon"><i className={theme.icon}></i></div>
                <div className="ah-tier-name">{pack.name}</div>
                <div className="ah-tier-kind">{theme.name}</div>
            </div>

            <div className="ah-tier-body">
                <div className="ah-tier-price">
                    <span className="ah-tier-currency">$</span>
                    <span className="ah-tier-amount">{pack.price.toLocaleString("en-US")}</span>
                </div>
                {bonusPct > 0 && (
                    <div className="ah-tier-bonus">+{bonusPct}% bonus credits</div>
                )}

                {pack.description && (
                    <p className="ah-tier-desc">{pack.description}</p>
                )}

                <ul className="ah-tier-features">
                    <li><i className="ri-coin-line" /><span><strong>{pack.credit.toLocaleString("en-US")}</strong> credits</span></li>
                    <li><i className="ri-infinity-line" /><span>Credits never expire</span></li>
                    <li><i className="ri-checkbox-circle-line" /><span>Access to all APIs</span></li>
                </ul>

                <Button
                    className="ah-tier-cta w-100 fw-medium"
                    onClick={onSelect}
                >
                    Get Started <i className="ri-arrow-right-line ms-1" />
                </Button>
            </div>
        </div>
    );
};

export default Plan;
